import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, MapPin, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CITIES = ['Athens, GA', 'Atlanta, GA', 'Savannah, GA', 'Nashville, TN', 'Other'];

export default function UserTypeModal() {
  const { user, refreshUser } = useAuth();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(1); // 1=type, 2=details
  const [userType, setUserType] = useState('');
  const [city, setCity] = useState('Athens, GA');
  const [schoolName, setSchoolName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only show user-type modal if phone is already set (PhoneRequiredModal handles phone first)
    if (user && !user.user_type && user.phone) {
      setTimeout(() => setShow(true), 800);
    }
  }, [user]);

  if (!show || !user) return null;

  const handleSubmit = async () => {
    if (!userType) return;
    setLoading(true);
    try {
      await fetch(`${API}/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ user_type: userType, city, school_name: schoolName || null }),
      });
      await refreshUser();
      setShow(false);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[999] bg-[#2B2B2B]/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
      <div
        className="bg-white rounded-3xl w-full shadow-2xl overflow-hidden"
        style={{ maxWidth: '460px' }}
        data-testid="user-type-modal"
      >
        {/* Header */}
        <div className="bg-[#2B2B2B] px-7 py-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C8A97E] mb-1">Welcome to Perkly</p>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Hi {user.name?.split(' ')[0]}! Tell us about yourself
          </h2>
          <p className="text-white/55 text-sm mt-1">So we can show you the most relevant deals near you.</p>
        </div>

        <div className="px-7 py-6">
          {step === 1 && (
            <>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-4">I am a…</p>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { id: 'student', label: 'Student', desc: 'College or university student', icon: GraduationCap },
                  { id: 'local', label: 'Local', desc: 'Athens resident or visitor', icon: MapPin },
                ].map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setUserType(t.id)}
                    data-testid={`user-type-${t.id}`}
                    className={`p-5 rounded-2xl border-2 text-left transition-all ${
                      userType === t.id
                        ? 'border-[#C8A97E] bg-[#C8A97E]/8'
                        : 'border-[#8A9A8B]/20 hover:border-[#C8A97E]/40'
                    }`}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#C8A97E]/15 flex items-center justify-center mb-3">
                      <t.icon size={18} className="text-[#C8A97E]" />
                    </div>
                    <p className="font-bold text-[#2B2B2B] text-sm">{t.label}</p>
                    <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{t.desc}</p>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                disabled={!userType}
                data-testid="user-type-next"
                className="w-full btn-primary justify-center py-3.5 disabled:opacity-50"
              >
                Continue <ArrowRight size={15} />
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] mb-4">
                {userType === 'student' ? 'Student Details' : 'Your Location'}
              </p>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">City</label>
                  <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="perkly-input"
                    data-testid="user-city-select"
                  >
                    {CITIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                {userType === 'student' && (
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">
                      School Name <span className="text-[#8A9A8B]/60 normal-case font-normal">(optional)</span>
                    </label>
                    <input
                      value={schoolName}
                      onChange={(e) => setSchoolName(e.target.value)}
                      placeholder="e.g. University of Georgia"
                      className="perkly-input"
                      data-testid="user-school-input"
                    />
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn-secondary flex-1 text-sm py-3 justify-center">Back</button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  data-testid="user-type-submit"
                  className="btn-primary flex-1 text-sm py-3 justify-center disabled:opacity-60"
                >
                  {loading ? 'Saving...' : "Let's Go!"}
                </button>
              </div>
              <button
                onClick={() => setShow(false)}
                className="w-full text-center text-xs text-[#8A9A8B] mt-3 hover:text-[#2B2B2B] transition-colors"
              >
                Skip for now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
